import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiEdit2, FiTrash2, FiXCircle, FiPlus } from 'react-icons/fi';
import type { Installation, User } from 'src/types';
import type { AlertSummaryEntry } from 'src/queries';
import AlertBadge from './AlertBadge';
import './ProjectExpandedRow.css';

interface ProjectExpandedRowProps {
  // Installations belonging to this project (already resolved from
  // project.installation_ids by the parent).
  installations: Installation[];
  // Users with access to this project (parent filters users by project_ids).
  usersWithAccess: User[];
  // Installations not yet on this project — candidates for "attach existing".
  attachableInstallations: Installation[];
  // Status maps keyed by installation id (from the bulk status hooks).
  latestTimes?: Record<string, string>;
  alertSummary?: Record<string, AlertSummaryEntry>;
  onCreateInstallation: () => void;
  onEditInstallation: (inst: Installation) => void;
  onRemoveFromProject: (inst: Installation) => void;
  onDeleteInstallation: (inst: Installation) => void;
  onAttachInstallation: (installationId: string) => void;
}

function formatTimestamp(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString([], {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const ProjectExpandedRow = ({
  installations,
  usersWithAccess,
  attachableInstallations,
  latestTimes,
  alertSummary,
  onCreateInstallation,
  onEditInstallation,
  onRemoveFromProject,
  onDeleteInstallation,
  onAttachInstallation,
}: ProjectExpandedRowProps) => {
  const { t } = useTranslation();
  const [attachOpen, setAttachOpen] = useState(false);

  return (
    <div className="project-expanded">
      {/* Users-with-access summary line */}
      <div className="project-expanded-access">
        <span className="project-expanded-access-label">
          {t('management.project.accessUsers')}:
        </span>{' '}
        {usersWithAccess.length === 0 ? (
          <span className="project-expanded-muted">{t('management.project.noAccessUsers')}</span>
        ) : (
          <span className="project-expanded-access-names">
            {usersWithAccess.map((u) => u.name || u.email).join(', ')}
          </span>
        )}
      </div>

      {/* Installations of this project */}
      <div className="project-expanded-installations">
        {installations.length === 0 ? (
          <p className="project-expanded-muted">{t('management.project.noInstallations')}</p>
        ) : (
          <table className="project-installations-table">
            <tbody>
              {installations.map((inst) => (
                <tr key={inst.id}>
                  <td className="project-installation-name">{inst.name}</td>
                  <td className="project-installation-status">
                    <span
                      className="project-installation-time"
                      title={t('management.installation.latestMeasurement')}
                    >
                      {formatTimestamp(latestTimes?.[inst.id])}
                    </span>
                  </td>
                  <td className="project-installation-alert">
                    <AlertBadge summary={alertSummary?.[inst.id]} />
                  </td>
                  <td className="project-installation-actions">
                    <button
                      className="data-table-action-btn"
                      onClick={() => onEditInstallation(inst)}
                      aria-label={t('management.edit')}
                      title={t('management.edit')}
                    >
                      <FiEdit2 />
                    </button>
                    <button
                      className="data-table-action-btn"
                      onClick={() => onRemoveFromProject(inst)}
                      aria-label={t('management.project.removeFromProject')}
                      title={t('management.project.removeFromProject')}
                    >
                      <FiXCircle />
                    </button>
                    <button
                      className="data-table-action-btn data-table-action-btn--danger"
                      onClick={() => onDeleteInstallation(inst)}
                      aria-label={t('management.delete')}
                      title={t('management.delete')}
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add: create-new (primary) + attach-existing (secondary) */}
      <div className="project-expanded-add">
        <button
          className="management-btn management-btn--primary management-btn--sm"
          onClick={onCreateInstallation}
        >
          <FiPlus /> {t('management.project.addNewInstallation')}
        </button>

        {attachableInstallations.length > 0 &&
          (attachOpen ? (
            <span className="project-attach-picker">
              <select
                className="project-attach-select"
                defaultValue=""
                onChange={(e) => {
                  if (e.target.value) {
                    onAttachInstallation(e.target.value);
                    setAttachOpen(false);
                  }
                }}
              >
                <option value="" disabled>
                  {t('management.project.selectInstallation')}
                </option>
                {attachableInstallations.map((inst) => (
                  <option key={inst.id} value={inst.id}>
                    {inst.name}
                  </option>
                ))}
              </select>
              <button
                className="management-btn management-btn--secondary management-btn--sm"
                onClick={() => setAttachOpen(false)}
              >
                {t('management.cancel')}
              </button>
            </span>
          ) : (
            <button
              className="management-btn management-btn--secondary management-btn--sm"
              onClick={() => setAttachOpen(true)}
            >
              {t('management.project.attachExisting')}
            </button>
          ))}
      </div>
    </div>
  );
};

export default ProjectExpandedRow;
